// ============================================================================
// PerCrewScheduler — the continuous per-crew scheduling authoring UI.
// r2-cs-scheduler-3e9b1d64
//
// Controlled sub-component of JobFormModal. Renders one scheduling section per
// crew that appears on the job (from lineItemCrews). Each crew section has:
//   1. header (crew name)
//   2. a cadence row  (day-of-week chips + start/end date + within-day time)
//        -> "Generate" expands to explicit appearances via the shipped engine
//   3. an editable list of the crew's appearances (each removable)
//   4. "+ Add day"   (a single manual appearance outside the cadence)
//   5. a per-crew mini preview (count + span)
//
// The authored schedule lives in the PARENT (JobFormModal) as `crewSchedule`
// state so it can be saved. This component receives it + a setter and edits it.
// Ephemeral cadence-row inputs live locally, keyed by crewGroupId.
//
// Appearances in state carry JS Dates for startAt/endAt (UI-editable). The
// parent's SAVE converts them to Firestore Timestamps and derives the envelope.
// This component never touches recurrence.js and never persists anything.
// ============================================================================

import React, { useState } from 'react';
import { Calendar, Users, Clock, Plus, X, AlertTriangle } from 'lucide-react';
import { buildAppearancesFromCadence, fromDateKey } from '../../utils/crewSchedule';

const DOW_CHIPS = [
  { token: 'Sun', label: 'S' },
  { token: 'Mon', label: 'M' },
  { token: 'Tue', label: 'T' },
  { token: 'Wed', label: 'W' },
  { token: 'Thu', label: 'T' },
  { token: 'Fri', label: 'F' },
  { token: 'Sat', label: 'S' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const toDateInputValue = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// "7:00a" style label from a JS Date
const fmtTime = (d) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${pad2(m)}${ap}`;
};
// "Wed, Jul 8" style label
const fmtDay = (d) => d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

// parse an <input type=time> "HH:MM" into {hour, minute}
const parseTimeInput = (val) => {
  const [h, m] = (val || '').split(':').map((n) => parseInt(n, 10));
  return { hour: Number.isNaN(h) ? 8 : h, minute: Number.isNaN(m) ? 0 : m };
};

// r2-cs-guard-endstart-8f3d1a6c
// End must be strictly after start (both "HH:MM"). Same-day block; overnight
// is intentionally unsupported — an inverted range is the bug this guards.
// Validates the PARSED time (via parseTimeInput) so "what we validate" is
// byte-identical to "what the handlers build".
const isEndAfterStart = (startVal, endVal) => {
  const s = parseTimeInput(startVal);
  const e = parseTimeInput(endVal);
  return e.hour * 60 + e.minute > s.hour * 60 + s.minute;
};

// default ephemeral cadence-row state for one crew.
// r2-cs-seeddate-9b3e7a41 — seedDate (the job's Multi-day Start Date, a
// 'YYYY-MM-DD' string) seeds the cadence "From" and the "Add a day" date so a
// crew doesn't default to today when the job starts on a chosen day. Falls back
// to today when absent. Untouched crews re-derive this each render, so changing
// the job's start date live-follows until the user edits a given crew's row.
const defaultBuilder = (seedDate, seedEndDate) => {
  const today = new Date();
  const in1wk = new Date(); in1wk.setDate(in1wk.getDate() + 6); // natural one-week starting span
  const seed = seedDate || toDateInputValue(today);
  // r2-cs-seedend-4d8a1c73 — seed the cadence "To" from the job's end date
  // (start + duration − 1), falling back to the one-week span when absent. Same
  // untouched-crew live-follow model as the start seed; edited crews hold.
  const seedEnd = seedEndDate || toDateInputValue(in1wk);
  return {
    daysOfWeek: [],
    startDate: seed,
    endDate: seedEnd,
    startTime: '08:00',
    endTime: '17:00',
    // "+ Add day" form has its OWN date + time, independent of the cadence row.
    // The time is REMEMBERED across adds within the session (seeded from cadence
    // defaults, then overwritten with whatever the user last entered).
    addDate: seed,
    addStartTime: '08:00',
    addEndTime: '17:00',
  };
};

export default function PerCrewScheduler({ crews, crewSchedule, setCrewSchedule, isReadOnly, getAppearanceConflict, onShowConflict, defaultDate, defaultEndDate }) {
  // ephemeral per-crew cadence-row inputs, keyed by crewGroupId
  const [builders, setBuilders] = useState({});

  // r2-cs-seeddate-9b3e7a41 — untouched crews re-derive the default (seeded from
  // the job's start date) each render; once a crew is edited, its stored entry wins.
  const getBuilder = (crewId) => builders[crewId] || defaultBuilder(defaultDate, defaultEndDate);
  const setBuilder = (crewId, patch) =>
    setBuilders((prev) => ({ ...prev, [crewId]: { ...getBuilder(crewId), ...patch } }));

  const appearancesFor = (crewId) => crewSchedule?.[crewId]?.appearances || [];

  const writeAppearances = (crewId, appearances) => {
    // keep sorted by startAt for a tidy list + correct preview
    const sorted = [...appearances].sort((a, b) => a.startAt - b.startAt);
    setCrewSchedule((prev) => ({
      ...prev,
      [crewId]: { appearances: sorted },
    }));
  };

  const toggleDow = (crewId, token) => {
    if (isReadOnly) return;
    const b = getBuilder(crewId);
    const has = b.daysOfWeek.includes(token);
    setBuilder(crewId, {
      daysOfWeek: has ? b.daysOfWeek.filter((t) => t !== token) : [...b.daysOfWeek, token],
    });
  };

  const handleGenerate = (crewId) => {
    if (isReadOnly) return;
    const b = getBuilder(crewId);
    // r2-cs-guard-endstart-8f3d1a6c — block an inverted cadence time-range
    if (!isEndAfterStart(b.startTime, b.endTime)) return;
    const st = parseTimeInput(b.startTime);
    const et = parseTimeInput(b.endTime);
    const generated = buildAppearancesFromCadence({
      startDate: b.startDate,
      endDate: b.endDate,
      daysOfWeek: b.daysOfWeek,
      time: {
        startHour: st.hour, startMinute: st.minute,
        endHour: et.hour, endMinute: et.minute,
        schedulingMode: 'window',
        arrivalWindow: null,
      },
    });
    // merge with any existing, de-dupe by date+time key
    const existing = appearancesFor(crewId);
    const seen = new Set(existing.map((a) => `${a.date}|${a.startAt.getTime()}`));
    const merged = [...existing];
    for (const a of generated) {
      const k = `${a.date}|${a.startAt.getTime()}`;
      if (!seen.has(k)) { merged.push(a); seen.add(k); }
    }
    writeAppearances(crewId, merged);
  };

  const handleAddDay = (crewId) => {
    if (isReadOnly) return;
    const b = getBuilder(crewId);
    // r2-cs-guard-endstart-8f3d1a6c — block an inverted add-day time-range
    if (!isEndAfterStart(b.addStartTime, b.addEndTime)) return;
    const base = fromDateKey(b.addDate);
    if (!base) return;
    const st = parseTimeInput(b.addStartTime);
    const et = parseTimeInput(b.addEndTime);
    const startAt = new Date(base.getFullYear(), base.getMonth(), base.getDate(), st.hour, st.minute, 0, 0);
    const endAt = new Date(base.getFullYear(), base.getMonth(), base.getDate(), et.hour, et.minute, 0, 0);
    const appearance = {
      id: `ap-${b.addDate}-manual-${Date.now()}`,
      date: b.addDate,
      startAt,
      endAt,
      schedulingMode: 'window',
      arrivalWindow: null,
    };
    // avoid exact duplicate (same date+time)
    const existing = appearancesFor(crewId);
    const dup = existing.some((a) => a.date === appearance.date && a.startAt.getTime() === startAt.getTime());
    writeAppearances(crewId, dup ? existing : [...existing, appearance]);
    // Remember the last-entered add time as the default for the next add
    // (leave addDate as-is so the user can keep adding around the same period).
    setBuilder(crewId, { addStartTime: b.addStartTime, addEndTime: b.addEndTime });
  };

  const handleRemove = (crewId, appearanceId) => {
    if (isReadOnly) return;
    writeAppearances(crewId, appearancesFor(crewId).filter((a) => a.id !== appearanceId));
  };

  // per-crew preview: "9 days · Jul 6 → Sep 4"
  const previewFor = (crewId) => {
    const apps = appearancesFor(crewId);
    if (apps.length === 0) return null;
    const starts = apps.map((a) => a.startAt.getTime());
    const min = new Date(Math.min(...starts));
    const max = new Date(Math.max(...starts));
    const span = apps.length === 1 ? fmtDay(min) : `${fmtDay(min)} → ${fmtDay(max)}`;
    return `${apps.length} day${apps.length === 1 ? '' : 's'} · ${span}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Calendar size={15} className="text-purple-600 mt-0.5" />
        <p className="text-xs text-gray-600">
          Schedule each crew on their own days across the job. Generate a repeating pattern
          (e.g. Mon/Wed/Fri), then add or remove individual days as needed. All crews stay on
          this one job and one invoice.
        </p>
      </div>

      {crews.map((crew) => {
        const b = getBuilder(crew.crewGroupId);
        const apps = appearancesFor(crew.crewGroupId);
        const preview = previewFor(crew.crewGroupId);
        // r2-cs-guard-endstart-8f3d1a6c — live validity drives disable + hint
        const cadenceValid = isEndAfterStart(b.startTime, b.endTime);
        const addValid = isEndAfterStart(b.addStartTime, b.addEndTime);
        return (
          <div key={crew.crewGroupId} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            {/* Crew header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
              <Users size={14} className="text-gray-500" />
              <span className="text-sm font-bold text-gray-700">{crew.crewGroupName || 'Unnamed crew'}</span>
              {preview && <span className="ml-auto text-xs font-medium text-purple-600">{preview}</span>}
            </div>

            <div className="p-3 space-y-3">
              {/* Cadence row */}
              {!isReadOnly && (
                <div className="space-y-2">
                  {/* Day-of-week chips */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 w-14">Repeat on</span>
                    <div className="flex gap-1">
                      {DOW_CHIPS.map((chip, i) => {
                        const active = b.daysOfWeek.includes(chip.token);
                        return (
                          <button
                            key={`${crew.crewGroupId}-dow-${i}`}
                            type="button"
                            onClick={() => toggleDow(crew.crewGroupId, chip.token)}
                            className={`w-7 h-7 rounded-full text-xs font-bold transition-colors ${active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                          >
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Date range + times */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-0.5">From</label>
                      <input type="date" value={b.startDate}
                        onChange={(e) => setBuilder(crew.crewGroupId, { startDate: e.target.value })}
                        className="w-full p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-0.5">To</label>
                      <input type="date" value={b.endDate}
                        onChange={(e) => setBuilder(crew.crewGroupId, { endDate: e.target.value })}
                        className="w-full p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-0.5">Start time</label>
                      <input type="time" value={b.startTime}
                        onChange={(e) => setBuilder(crew.crewGroupId, { startTime: e.target.value })}
                        className="w-full p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-0.5">End time</label>
                      <input type="time" value={b.endTime}
                        onChange={(e) => setBuilder(crew.crewGroupId, { endTime: e.target.value })}
                        className="w-full p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                  </div>

                  {/* r2-cs-guard-endstart-8f3d1a6c */}
                  {!cadenceValid && (
                    <p className="text-[11px] text-red-500">End must be after start.</p>
                  )}
                  <button
                    type="button"
                    onClick={() => handleGenerate(crew.crewGroupId)}
                    disabled={b.daysOfWeek.length === 0 || !cadenceValid}
                    className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                  >
                    Generate days
                  </button>
                </div>
              )}

              {/* Appearance list */}
              {apps.length > 0 && (
                <div className="space-y-1">
                  {apps.map((a) => {
                    // r2-cs-conflict-wire-5b2e8c94 — live crew double-booking indicator
                    const conflict = getAppearanceConflict?.(crew.crewGroupId, a.id) || null;
                    return (
                    <div key={a.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${conflict ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <Clock size={12} className="text-gray-400" />
                      <span className="text-sm text-gray-700">{fmtDay(a.startAt)}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className={`text-xs ${conflict ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{fmtTime(a.startAt)}–{fmtTime(a.endAt)}</span>
                      {conflict && (
                        <button
                          type="button"
                          onClick={() => onShowConflict?.({ ...conflict, crewName: crew.crewGroupName || 'This crew' })}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Scheduling conflict — click for details"
                        >
                          <AlertTriangle size={14} />
                        </button>
                      )}
                      {!isReadOnly && (
                        <button type="button" onClick={() => handleRemove(crew.crewGroupId, a.id)}
                          className="ml-auto text-gray-300 hover:text-red-500 transition-colors">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {apps.length === 0 && (
                <p className="text-xs text-gray-400 italic">No days scheduled yet for this crew.</p>
              )}

              {/* + Add day — its own date + time (time is remembered across adds).
                  Doubles as the per-day custom-time tool: remove a generated day
                  and re-add it here at a different time. */}
              {!isReadOnly && (
                <div className="flex items-end gap-2 pt-1 flex-wrap">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">Add a day</label>
                    <input type="date" value={b.addDate}
                      onChange={(e) => setBuilder(crew.crewGroupId, { addDate: e.target.value })}
                      className="p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">Start</label>
                    <input type="time" value={b.addStartTime}
                      onChange={(e) => setBuilder(crew.crewGroupId, { addStartTime: e.target.value })}
                      className="p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">End</label>
                    <input type="time" value={b.addEndTime}
                      onChange={(e) => setBuilder(crew.crewGroupId, { addEndTime: e.target.value })}
                      className="p-1.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
                  </div>
                  {/* r2-cs-guard-endstart-8f3d1a6c */}
                  <button
                    type="button"
                    onClick={() => handleAddDay(crew.crewGroupId)}
                    disabled={!addValid}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={13} /> Add day
                  </button>
                  {!addValid && (
                    <p className="w-full text-[11px] text-red-500">End must be after start.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}